"use client";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Pencil, RotateCcw, Trash2 } from "lucide-react";
import type { activationCodeSourceEnum, activationCodeStatusEnum } from "@/server/db/schema/activation"; // For status type

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { ColumnDef } from "@tanstack/react-table";
import { ProductHoverCard } from "@/components/admin/activation/ProductHoverCard";
import type { TFunction } from "i18next"; // Correctly import TFunction

export interface ActivationCodeRow {
  id: string;
  code: string;
  productId: string;
  product?: { name: string; maxBindingsPerCode?: number | null };
  userId?: string | null;
  user?: { email?: string | null };
  assignedEmail?: string | null;
  status: (typeof activationCodeStatusEnum.enumValues)[number];
  source: (typeof activationCodeSourceEnum.enumValues)[number];
  expiresAt: string;
  activatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  activationCodeBindings: { id: string }[]; // For counting current bindings
}

interface ActivationCodeColumnsProps {
  onEdit: (code: ActivationCodeRow) => void;
  onDelete: (code: ActivationCodeRow) => void;
  onReset: (code: ActivationCodeRow) => void; // Add reset handler
  isAdmin: boolean; // Add isAdmin flag
  tFunction: TFunction;
}

export const getActivationCodeColumns = ({ onEdit, onDelete, onReset, isAdmin, tFunction }: ActivationCodeColumnsProps): ColumnDef<ActivationCodeRow>[] => {
  const t = tFunction; // Use the passed t function

  const getStatusVariant = (status: ActivationCodeRow["status"]) => {
    switch (status) {
      case "active": return "default"; // Greenish or primary
      case "available": return "secondary"; // Bluish or neutral
      case "assigned": return "outline"; // Neutral outline
      case "expired": return "destructive"; // Reddish
      case "revoked": return "destructive"; // Reddish variant or specific style
      default: return "outline";
    }
  };

  const getSourceVariant = (source: ActivationCodeRow["source"]) => {
    switch (source) {
      case "manual": return "outline"; // Neutral outline
      case "purchase": return "default"; // Greenish or primary
      case "api": return "secondary"; // Bluish
      default: return "outline";
    }
  };

  return [
    {
      accessorKey: "code",
      header: t("admin.activation.codes.codeLabel"),
      cell: ({ row }) => <div className="font-mono">{row.getValue("code")}</div>,
      size: 200,
    },
    {
      accessorKey: "product.name",
      header: t("admin.activation.codes.productLabel"),
      cell: ({ row }) => {
        // 如果没有产品名称，显示N/A
        if (!row.original.product?.name) {
          return <span className="text-muted-foreground">{t("common.na")}</span>;
        }

        // 如果有产品ID和名称，使用ProductHoverCard组件
        if (row.original.productId && row.original.product?.name) {
          return (
            <ProductHoverCard productId={row.original.productId}>
              <span className="cursor-help underline underline-offset-4 decoration-dotted">
                {row.original.product.name}
              </span>
            </ProductHoverCard>
          );
        }

        // 兜底返回产品名称
        return row.original.product.name;
      },
      size: 180,
    },
    {
      accessorKey: "source",
      header: t("admin.activation.codes.sourceLabel"),
      cell: ({ row }) => {
        const source = row.getValue("source") as ActivationCodeRow["source"];
        return <Badge variant={getSourceVariant(source)}>{t(`admin.activationCodeSource.${source}`)}</Badge>;
      },
      size: 120,
    },
    {
      accessorKey: "assignedEmail",
      header: t("admin.activation.codes.assignedEmailLabel"),
      cell: ({ row }) => row.getValue("assignedEmail") ?? <span className="text-muted-foreground">{t("common.na")}</span>,
      size: 200,
    },
    {
      accessorKey: "status",
      header: t("admin.activation.codes.statusLabel"),
      cell: ({ row }) => {
        const status = row.getValue("status") as ActivationCodeRow["status"];
        return <Badge variant={getStatusVariant(status)}>{t(`admin.activationCodeStatus.${status}`)}</Badge>;
      },
      size: 120,
    },
    {
      accessorKey: "expiresAt",
      header: t("admin.activation.codes.expiresAtLabel"),
      cell: ({ row }) => new Date(row.getValue("expiresAt")).toLocaleDateString(),
      size: 150,
    },
    {
      accessorKey: "bindings",
      header: t("admin.activation.codes.bindingsUsedLabel"), // Add this translation key
      cell: ({ row }) => {
        const currentBindings = row.original.activationCodeBindings?.length ?? 0;
        const maxBindings = row.original.product?.maxBindingsPerCode;
        let displayText = `${currentBindings} / `;
        if (maxBindings === null || maxBindings === undefined) {
          displayText += t("common.unknown");
        } else if (maxBindings === 0) {
          displayText += t("common.unlimited");
        } else {
          displayText += maxBindings;
        }
        return <div className="text-center">{displayText}</div>;
      },
      size: 130,
    },
    {
      accessorKey: "createdAt",
      header: t("settings.apiKeys.table.createdAt"),
      cell: ({ row }) => new Date(row.getValue("createdAt")).toLocaleDateString(),
      size: 150,
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("common.actions")}</div>,
      cell: ({ row }) => {
        const code = row.original;
        return (
          <div className="text-right">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">{t("admin.users.columns.openMenu")}</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onEdit(code)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("settings.apiKeys.actions.edit")}
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => onReset(code)}>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    {t("admin.activation.codes.actions.reset")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDelete(code)} className="text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("settings.apiKeys.actions.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      size: 80,
    },
  ];
};